import { useRef, useState } from "react";
import { toast } from "sonner";
import GameShell from "@/components/GameShell";
import BetControls from "@/components/BetControls";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";

const SYMBOLS = ["₿", "Ξ", "◎", "₮", "🪙", "💎", "7", "★"];

function Reel({ symbol, spinning }) {
  return (
    <div className="slot-reel overflow-hidden">
      {spinning ? (
        <div className="flex flex-col spinning">
          {[...SYMBOLS, ...SYMBOLS].map((s, i) => (
            <div key={i} className="h-[120px] flex items-center justify-center">
              <span className="gold-text">{s}</span>
            </div>
          ))}
        </div>
      ) : (
        <span className="gold-text">{symbol}</span>
      )}
    </div>
  );
}

export default function Slots() {
  const { setUser, user } = useAuth();
  const { currency } = useCurrency();
  const [amount, setAmount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [reels, setReels] = useState(["💎", "💎", "💎"]);
  const [spinning, setSpinning] = useState([false, false, false]);
  const [last, setLast] = useState(null);
  const timers = useRef([]);

  const placeBet = (betAmount, quiet = false) => new Promise(async (resolve) => {
    setBusy(true);
    setSpinning([true, true, true]);
    try {
      const { data } = await api.post("/games/slots", {
        amount: Number(betAmount),
        currency,
      });
      const final = data.bet.result.reels;
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = final.map((sym, i) =>
        setTimeout(() => {
          setReels((prev) => {
            const n = [...prev];
            n[i] = sym;
            return n;
          });
          setSpinning((prev) => {
            const n = [...prev];
            n[i] = false;
            return n;
          });
          if (i === final.length - 1) {
            setUser((u) => ({ ...u, balances: data.balances }));
            setLast(data.bet);
            setBusy(false);
            if (!quiet) {
              if (data.bet.profit > 0) toast.success(`×${data.bet.multiplier} · +${formatAmount(data.bet.profit, currency)}`);
              else toast.error("No match");
            }
            resolve(data.bet);
          }
        }, (quiet ? 400 : 800) + i * (quiet ? 250 : 500))
      );
    } catch (e) {
      setBusy(false);
      setSpinning([false, false, false]);
      toast.error(e?.response?.data?.detail || "Failed");
      resolve(null);
    }
  });

  const play = async () => {
    if (!user) return;
    if (Number(amount) <= 0) return toast.error("Enter a bet");
    await placeBet(amount);
  };

  return (
    <GameShell title="Slots" subtitle="Three reels. One jackpot. Zero mercy." accent="#D97706"
      aside={
        <BetControls
          amount={amount}
          setAmount={setAmount}
          onPlay={play}
          autoPlaceBet={(a) => placeBet(a, true)}
          busy={busy}
          disabled={!user || busy}
          playLabel="Spin Reels"
        />
      }
    >
      <div className="h-[460px] flex flex-col items-center justify-center gap-8">
        <div className="relative panel p-6 flex gap-4 bg-gradient-to-b from-[#1E1E24] to-[#0F0F11]" data-testid="slots-reels">
          {reels.map((s, i) => (
            <Reel key={i} symbol={s} spinning={spinning[i]} />
          ))}
        </div>
        <div className="text-xs uppercase tracking-widest text-zinc-500" data-testid="slots-status">
          {busy
            ? "Spinning…"
            : last
            ? last.result.won
              ? `×${last.multiplier} · ${formatAmount(last.payout, currency)} ${currency}`
              : "No match. Spin again."
            : "Pull the lever"}
        </div>

        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 w-full max-w-xl">
          {SYMBOLS.map((s) => (
            <div key={s} className="panel py-2 text-center font-display text-xl gold-text">
              {s}
            </div>
          ))}
        </div>
      </div>
    </GameShell>
  );
}
